import { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { useContactUsContext } from "../state/contact_us_state";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../../../../core/constants/app_constants";

const SAMPLE_INFORMATION_DATA = [
  // {
  //     title: 'Address',
  //     description: '22647 VENTURA BLVD. WOODLAND HILLS, CA 91367',
  //     icon: 'pi pi-map-marker'
  // },
  // {
  //     title: 'Phone',
  //     description: '+12 912 123 1676',
  //     icon: 'pi pi-phone'
  // },
  {
    title: "Email",
    description: "support@erotixai.com",
    icon: "pi pi-envelope",
  },
];

const ContactUsComp = () => {
  // State for fields and errors
  const { name, email, message, errors, isLoading, handleSubmit, setName, setEmail, setMessage, setErrors, setIsLoading, setRecaptchaToken, onRecaptchaError, isSubmitEnabled, isSuccess } = useContactUsContext();

  const recaptchaRef = useRef(null);

  useEffect(() => {
    if(isSuccess){
      recaptchaRef.current.reset();
    }
  }, [isSuccess]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-fuchsia-950 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            We're here for you — anytime, anywhere.
          </h1>
          <p className="text-lg text-pink-300 max-w-3xl mx-auto">
            Have a question, feedback, or need support? Our team is ready to help you make the most of your experience with ErotiX AI.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info Items Section */}
          <div className="space-y-8">
            {SAMPLE_INFORMATION_DATA.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 bg-slate-900/50 backdrop-blur-sm rounded-lg border border-red-900/30">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-800 to-red-950 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-white text-xl`}></i>
                  </div>
                </div>
                <div>
                  <h5 className="text-lg font-semibold text-white mb-1">{item.title}</h5>
                  <p className="text-pink-300">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form Section */}
          <div className="relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-900/0 via-red-700 to-red-900/0"></div>
            <form onSubmit={handleSubmit}>
              <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md rounded-lg p-8 border border-red-900/30 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-6">Send us a message</h3>
                <div className="space-y-4">
                  {/* Name Field */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-pink-400 z-10">
                      <i className="pi pi-user text-lg"></i>
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      placeholder="Full Name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>
                  {/* Email Field */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-pink-400 z-10">
                      <i className="pi pi-envelope text-lg"></i>
                    </div>
                    <input
                      type="email"
                      className="w-full pl-10 pr-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      placeholder="Email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>
                  {/* Message Field */}
                  <div className="relative">
                    <textarea
                      className="w-full px-3 py-3 bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg text-white placeholder-pink-300/70 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent min-h-[150px]"
                      placeholder="Message"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      required
                    />
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                    )}
                  </div>
                  <div className="relative">
                    <ReCAPTCHA 
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      render="explicit"
                      verifyCallback={setRecaptchaToken}
                      size="normal"
                      onChange={setRecaptchaToken}
                      onError={onRecaptchaError}
                    />
                  </div>
                  <div className="mt-6">
                    <Button
                      className="w-full py-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 text-white font-medium rounded-lg hover:from-red-700 hover:via-red-800 hover:to-red-900 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                      type="submit"
                      loading={isLoading}
                      disabled={isLoading || !isSubmitEnabled}
                      theme="dark"
                    >
                      {/* {isLoading && (
                        <i className="pi pi-spinner pi-spin" style={{ marginRight: "0.5rem" }}></i>
                      )} */}
                      <span>{isLoading ? 'Sending...' : 'Send'}</span>
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsComp;
